import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import { isV2Api } from "./api-contract"
import { useEvents } from "./active-event"
import {
  formatWallClockDate,
  formatWallClockTime,
  normalizeWallClockDateTime,
  parseWallClockDate,
  parseWallClockDateTime,
  wallClockDayKey,
} from "./local-datetime"
import type { DietaryAllergy, DietaryIntolerance, DietaryFlags } from "./people"
import type { V2Activity, V2ActivityRegistration } from "./v2/contracts"
import {
  cancelActivityRegistration,
  createV2Activity,
  getActivity,
  listAllActivities,
  listAllActivityRegistrations,
  publishV2Activity,
  promoteNextActivityWaitlistRegistration,
  registerEventPersonForActivity,
} from "./v2/organizer"
import { v2Api } from "./v2/client"

// Schedule + Session-record data layer. Query keys for the LIST resources match
// apps/admin byte-for-byte (`["resource", <table>, {eventId}]`) so the vendored
// realtime layer surgically patches the grid/timeline caches on live CRUD.
// The Session record bundle (`activity-detail`) is keyed by agendaItemId; its
// lifecycle mutations invalidate both it and the agenda-items resource cache.

// ── list-resource row types (mirror apps/admin SchedulePage) ────────────────
export type AgendaItem = {
  id: string
  eventId: string
  groupId: string | null
  activityId: string | null
  locationId: string | null
  position: number
  name: string
  description: string | null
  startsAt: string
  endsAt: string
  capacity: number | null
  accessMode: string
  visibility: "public" | "private" | "cohort" | "organizer" | string
  audienceGroupId: string | null
  preApproved: number
  isRequired: number
  rsvpMode: string
  code: string | null
  status: string
}

export type AgendaGroup = {
  id: string
  eventId?: string
  name: string
  description?: string | null
  selectionRule: "one" | "multi" | string
  position: number
  isRequired?: number
  /** 1 = the group offers an explicit "None of these" opt-out. */
  allowNone?: number
  /** Group-level slot time, naive-local "YYYY-MM-DDTHH:mm" (may be null). Used
   *  to disambiguate same-named groups in pickers. */
  startsAt?: string | null
  endsAt?: string | null
}

/** Compact picker label for a group: "Saturday Morning · Sat Sep 12, 8:00am".
 *  Falls back to just the name when the group has no slot time. Because group
 *  names aren't unique, the day+time suffix is what makes options tell-apart-able
 *  in a flat `<select>`. */
export function groupPickerLabel(g: AgendaGroup): string {
  const day = (g.startsAt ?? "").slice(0, 10)
  if (!day) return g.name
  const when = `${formatWeekday(day)} ${formatDayMonth(day)}, ${formatTime(g.startsAt!)}`
  return `${g.name} · ${when}`
}

export type AgendaActivity = {
  id: string
  eventId?: string
  name: string
  description?: string | null
  minPerAttendee: number | null
  maxPerAttendee: number | null
  isRequired?: number
  position?: number
}

export type LocationRow = {
  id: string
  parentLocationId: string | null
  name: string
  position: number
}

type ListEnvelope<T> = { data: T[] }

// ── query keys (must match apps/admin) ──────────────────────────────────────
export const agendaItemsKey = (eventId: string) =>
  ["resource", "agenda-items", { eventId }] as const
export const agendaGroupsKey = (eventId: string) =>
  ["resource", "agenda-groups", { eventId, mode: "schedule" }] as const
export const agendaActivitiesKey = (eventId: string) =>
  ["resource", "agenda-activities", { eventId, mode: "schedule" }] as const
export const locationsKey = (eventId: string) =>
  ["resource", "locations", { eventId, mode: "schedule" }] as const

// ── list query options ──────────────────────────────────────────────────────
export function agendaItemsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaItemsKey(eventId),
    queryFn: async () => {
      if (isV2Api) {
        const activities = await listAllActivities(eventId)
        return { data: activities.map(activityToAgendaItem) }
      }
      return api<ListEnvelope<AgendaItem>>("/api/v1/agenda-items", {
        searchParams: { limit: 500, eventId, sort: "startsAt", order: "asc" },
      })
    },
    select: (r) => r.data,
  })
}

export function agendaGroupsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaGroupsKey(eventId),
    queryFn: () => api<ListEnvelope<AgendaGroup>>("/api/v1/agenda-groups", {
          searchParams: { limit: 200, eventId },
        }),
    select: (r) => r.data,
  })
}

export function agendaActivitiesQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaActivitiesKey(eventId),
    queryFn: () => api<ListEnvelope<AgendaActivity>>("/api/v1/agenda-activities", {
          searchParams: { limit: 200, eventId },
        }),
    select: (r) => r.data,
  })
}

export function locationsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: locationsKey(eventId),
    queryFn: () => api<ListEnvelope<LocationRow>>("/api/v1/locations", {
          searchParams: { limit: 500, eventId },
        }),
    select: (r) => r.data,
  })
}

// ── Session record (Activity HQ) bundle ─────────────────────────────────────
export type ActivityParticipant = {
  guestId: string
  rsvpId: string | null
  status: string
  respondedAt: string | null
  checkedInAt: string | null
  checkedOutAt: string | null
  preApproved: number
  waitlistPosition: number | null
  displayName: string
  emailAtInvite: string
  titleAtInvite: string | null
  companyAtInvite: string | null
  linkedUserId: string | null
  implicit?: true
  // Structured event-scoped dietary, projected onto this meal's participant.
  dietary?: ParticipantDietary
}

export type ParticipantDietary = {
  shared: true
  flags: DietaryFlags
  allergens: DietaryAllergy[]
  intolerances: DietaryIntolerance[]
  notes: string | null
  anaphylaxis: boolean
  source: string | null
  updatedAt: string | null
} | null

// Per-meal dietary rollup (matches the backend aggregator). Allergies and
// intolerances are kept numerically distinct; the anaphylaxis count is the
// only safety-critical sub-distinction.
export type DietaryRollup = {
  total: number
  sharedCount: number
  notSharedCount: number
  plateHeadcount: number
  tagCounts: Record<string, number>
  allergenCounts: { kind: string; group: string | null; total: number; anaphylaxisCount: number }[]
  allergenGroupCounts: { group: string; total: number; anaphylaxisCount: number }[]
  customAllergens: { label: string; total: number; anaphylaxisCount: number }[]
  intoleranceCounts: { kind: string; total: number }[]
  customIntolerances: { label: string; total: number }[]
  anaphylaxisFlags: { guestId: string; displayName: string; kinds: string[]; notes: string | null }[]
  notShared: { guestId: string; displayName: string }[]
}

export type ActivityTodo = {
  id: string
  title: string
  body: string | null
  hasForm: boolean
  responsePolicy: string | null
  createdAt: string
  eligibleCount: number
  completedCount: number
  pending: Array<{ guestId: string; displayName: string }>
}

export type ActivityChannel = {
  id: string
  name: string | null
  topic: string | null
  emoji: string | null
  archivedAt: string | null
}

export type ActivityDetail = {
  success: true
  item: {
    id: string
    eventId: string
    name: string
    description: string | null
    startsAt: string
    endsAt: string
    // Inline address columns were retired in favour of the locations FK; the
    // bundle still returns them when present (older rows) but they're often
    // absent — resolve the location via `locationId` against the locations list.
    venueName?: string | null
    streetAddress?: string | null
    addressLocality?: string | null
    locationId: string | null
    capacity: number | null
    accessMode: string
    rsvpMode: string
    isRequired: number
    preApproved: number
    reservationsOpenAt: string | null
    reservationsCloseAt: string | null
    groupId: string | null
    activityId: string | null
    code: string | null
    status: string
    visibility: "public" | "private" | "cohort" | "organizer" | string
    waitlistEnabled: number
    waitlistCapacity: number | null
  }
  group: { id: string; name: string } | null
  activity: { id: string; name: string; minPerAttendee: number | null; maxPerAttendee: number | null } | null
  rollup: {
    capacity: number | null
    confirmed: number
    pending: number
    declined: number
    checkedIn: number
    total: number
  }
  participants: ActivityParticipant[]
  dietaryRollup?: DietaryRollup
  todos: ActivityTodo[]
  channel: ActivityChannel | null
  state?: {
    state: "open" | "full" | "waitlist" | "closed" | "cancelled" | "not-invited"
    reason: string
  }
}

// Documents attached to a session (or its parent activity). The activity-detail
// bundle does NOT carry documents — the admin SPA fetches the event's full doc
// list separately and filters client-side, so we mirror that exactly (same
// endpoint + cache key as the Documents route, so the two share a cache entry).
export type AttachedDoc = {
  id: string
  slug: string
  title: string
  kind: string
  quickLink: number
  agendaItemId: string | null
  agendaActivityId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  currentSnapshot: { version: number; publishedAt: string } | null
}

export const eventDocumentsKey = (eventId: string) =>
  ["event-documents", eventId] as const

export function eventDocumentsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: eventDocumentsKey(eventId),
    queryFn: () => api<{ success: true; documents: AttachedDoc[] }>("/api/v1/event-documents/list", {
          method: "POST",
          body: { eventId },
        }),
    select: (r) => r.documents,
  })
}

export const activityDetailKey = (agendaItemId: string) =>
  ["activity-detail", agendaItemId] as const

export function activityDetailQueryOptions(eventId: string, agendaItemId: string) {
  return queryOptions({
    queryKey: activityDetailKey(agendaItemId),
    queryFn: async () => {
      if (isV2Api) {
        const [activity, registrations] = await Promise.all([
          getActivity(eventId, agendaItemId),
          listAllActivityRegistrations(eventId, agendaItemId),
        ])
        return activityToDetail(activity, registrations.registrations)
      }
      return api<ActivityDetail>("/api/v1/agenda-items/get-activity-detail", {
        method: "POST",
        body: { agendaItemId },
      })
    },
  })
}

export function activityToAgendaItem(activity: V2Activity): AgendaItem {
  return {
    id: activity.id,
    eventId: activity.eventId,
    groupId: null,
    activityId: null,
    locationId: null,
    position: 0,
    name: activity.name,
    description: activity.description,
    startsAt: activity.startsAt,
    endsAt: activity.endsAt,
    capacity: activity.maxCapacity,
    accessMode: "open",
    visibility: "public",
    audienceGroupId: null,
    preApproved: 1,
    isRequired: 0,
    rsvpMode: "rsvp",
    code: activity.slug,
    status: activity.status,
  }
}

export function activityToDetail(
  activity: V2Activity,
  registrations: V2ActivityRegistration[],
): ActivityDetail {
  const statusCount = (status: string) =>
    registrations.filter((registration) => registration.status === status).length
  const participants = registrations.map((registration): ActivityParticipant => ({
    guestId: registration.eventPersonId,
    rsvpId: registration.id,
    status: registration.status,
    respondedAt: registration.respondedAt,
    checkedInAt: null,
    checkedOutAt: null,
    preApproved: registration.status === "confirmed" ? 1 : 0,
    waitlistPosition: registration.waitlistPosition,
    displayName: registration.eventPerson.displayName,
    // Email is intentionally absent from the safe v2 activity-registration
    // DTO. Do not join or infer global identity on the client.
    emailAtInvite: "",
    titleAtInvite: registration.eventPerson.title,
    companyAtInvite: registration.eventPerson.company,
    linkedUserId: null,
  }))
  const confirmed = statusCount("confirmed")
  const pending = statusCount("pending") + statusCount("invited")
  const declined = statusCount("declined") + statusCount("cancelled")
  return {
    success: true,
    item: {
      id: activity.id,
      eventId: activity.eventId,
      name: activity.name,
      description: activity.description,
      startsAt: activity.startsAt,
      endsAt: activity.endsAt,
      venueName: activity.venueName,
      locationId: null,
      capacity: activity.maxCapacity,
      accessMode: "open",
      rsvpMode: "rsvp",
      isRequired: 0,
      preApproved: 1,
      reservationsOpenAt: null,
      reservationsCloseAt: null,
      groupId: null,
      activityId: null,
      code: activity.slug,
      status: activity.status,
      visibility: "public",
      waitlistEnabled: activity.maxCapacity === null ? 0 : 1,
      waitlistCapacity: null,
    },
    group: null,
    activity: null,
    rollup: {
      capacity: activity.maxCapacity,
      confirmed,
      pending,
      declined,
      checkedIn: 0,
      total: registrations.length,
    },
    participants,
    todos: [],
    channel: null,
    state: {
      state:
        activity.status === "cancelled" ? "cancelled"
          : activity.status === "closed" ? "closed"
            : activity.counts.remainingCapacity === 0 ? "full"
              : "open",
      reason: activity.status,
    },
  }
}

// ── create / edit callers (replace the disabled auto-CRUD on agenda_items) ───
// start/end are destination-LOCAL naive strings (YYYY-MM-DDTHH:mm:ss) per the
// app's local-time-only convention. Send only the fields you mean to change on
// update — omitted = unchanged, explicit null clears a nullable field.
export type CreateItemInput = {
  eventId: string
  name: string
  description?: string | null
  startsAt: string
  endsAt: string
  groupId?: string | null
  activityId?: string | null
  locationId?: string | null
  capacity?: number | null
  accessMode?: "open" | "invite-only"
  visibility?: "public" | "private" | "cohort" | "organizer"
  // Set with visibility="cohort" — the attendee group whose members can see +
  // RSVP. Passing it coerces visibility to "cohort"; null clears it.
  audienceGroupId?: string | null
  isRequired?: 0 | 1
  // 0 = RSVPs land in `pending` (host approves each); 1 = auto-confirmed.
  preApproved?: 0 | 1
  rsvpMode?: "required" | "rsvp" | "optional" | "display"
  // Reservation window — naive-local "YYYY-MM-DDTHH:mm:ss" strings (the action
  // enforces that exact shape). null clears the bound; undefined leaves it.
  reservationsOpenAt?: string | null
  reservationsCloseAt?: string | null
  // Short handle ([A-Z0-9-]{1,30}); omit/blank → server auto-generates from name.
  code?: string
  // Waitlist — only meaningful when capacity is set.
  waitlistEnabled?: 0 | 1
  waitlistCapacity?: number | null
  // Sort order within a group/activity (children pass their row index).
  position?: number
  dryRun?: boolean
}

export type UpdateItemInput = Partial<Omit<CreateItemInput, "eventId">> & {
  id: string
  dryRun?: boolean
}

export function createItem(input: CreateItemInput) {
  if (isV2Api) {
    const event = useEvents.getState().byId(input.eventId)
    const ianaTimezone = event?.ianaTimezone
    if (!ianaTimezone) {
      return Promise.reject(
        new Error("The v2 event catalog did not return an IANA timezone"),
      )
    }
    return createV2Activity({
      eventId: input.eventId,
      name: input.name.trim(),
      slug: slugifyActivity(input.code || input.name),
      description: input.description,
      startsAt: normalizeWallClockDateTime(input.startsAt),
      endsAt: normalizeWallClockDateTime(input.endsAt),
      ianaTimezone,
      maxCapacity: input.capacity,
    }).then((response) => ({
      success: true as const,
      item: activityToAgendaItem(response.activity),
    }))
  }
  return api<{
    success: true
    item?: AgendaItem
    dryRun?: true
    wouldCreate?: Record<string, unknown>
  }>("/api/v1/agenda-items/create", { method: "POST", body: input })
}

export function updateItem(input: UpdateItemInput) {
  if (isV2Api) {
    const eventId = requireActiveEventId()
    const patch: Record<string, unknown> = {}
    if (input.name !== undefined) patch.name = input.name.trim()
    if (input.description !== undefined) patch.description = input.description
    if (input.startsAt !== undefined) patch.startsAt = normalizeWallClockDateTime(input.startsAt)
    if (input.endsAt !== undefined) patch.endsAt = normalizeWallClockDateTime(input.endsAt)
    if (input.capacity !== undefined) patch.maxCapacity = input.capacity
    return v2Api<{ success?: true } & Partial<V2Activity>>(
      `/api/v2/events/${encodeURIComponent(eventId)}/activities/${encodeURIComponent(input.id)}`,
      { method: "PATCH", body: patch },
    ).then((activity) => ({
      success: true as const,
      activity,
    }))
  }
  return api<{
    success: true
    item?: AgendaItem
    unchanged?: true
    dryRun?: true
    wouldUpdate?: { id: string; patch: Record<string, unknown> }
  }>("/api/v1/agenda-items/update", { method: "POST", body: input })
}

// ── choice-group + activity create callers (parents for the agenda wizard) ───
// A group is a "pick one / pick any" header; an activity is a repeated offering
// with a per-attendee quota. The wizard creates the parent first, then fans out
// child agenda_items (groupId / activityId set) via createItem.
export type CreateGroupInput = {
  eventId: string
  name: string
  description?: string | null
  selectionRule: "one" | "multi"
  allowNone: 0 | 1
  startsAt: string | null
  endsAt: string | null
  isRequired: 0 | 1
  position?: number
  dryRun?: boolean
}

export type CreateActivityInput = {
  eventId: string
  name: string
  description?: string | null
  heroImageId?: string | null
  minPerAttendee: number
  maxPerAttendee: number
  isRequired: 0 | 1
  position?: number
  dryRun?: boolean
}

export function createGroup(input: CreateGroupInput) {
  return api<{ success?: true; group: { id: string } }>("/api/v1/agenda-groups/create", {
    method: "POST",
    body: input,
  })
}

export type UpdateGroupInput = Partial<Omit<CreateGroupInput, "eventId">> & {
  id: string
  shiftChildren?: boolean
  dryRun?: boolean
}

export function updateGroup(input: UpdateGroupInput) {
  return api<{
    success: true
    group?: AgendaGroup
    unchanged?: true
    dryRun?: true
    wouldUpdate?: { id: string; patch: Record<string, unknown> }
  }>("/api/v1/agenda-groups/update", { method: "POST", body: input })
}

export function createActivity(input: CreateActivityInput) {
  return api<{ success?: true; activity: { id: string } }>(
    "/api/v1/agenda-activities/create",
    { method: "POST", body: input },
  )
}

export type UpdateActivityInput = Partial<Omit<CreateActivityInput, "eventId">> & {
  id: string
  dryRun?: boolean
}

export function updateActivity(input: UpdateActivityInput) {
  return api<{
    success: true
    activity?: AgendaActivity
    unchanged?: true
    dryRun?: true
    wouldUpdate?: { id: string; patch: Record<string, unknown> }
  }>("/api/v1/agenda-activities/update", { method: "POST", body: input })
}

export function duplicateAgendaGroup(id: string) {
  return api<{
    success: true
    group: AgendaGroup
    items: AgendaItem[]
  }>("/api/v1/agenda-groups/duplicate", {
    method: "POST",
    body: { id },
  })
}

export type DeleteAgendaItemPreview = {
  success: true
  dryRun: true
  wouldDelete: {
    id: string
    name: string
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}

export function deleteAgendaItemDryRun(id: string) {
  return api<DeleteAgendaItemPreview>("/api/v1/agenda-items/delete", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function deleteAgendaItem(id: string) {
  return api<{
    success: true
    itemId: string
    deleted: Omit<DeleteAgendaItemPreview["wouldDelete"], "id" | "name">
  }>(
    "/api/v1/agenda-items/delete",
    { method: "POST", body: { id } },
  )
}

export type DeleteAgendaGroupPreview = {
  success: true
  dryRun: true
  wouldDelete: {
    id: string
    name: string
    items: number
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}

export function deleteAgendaGroupDryRun(id: string) {
  return api<DeleteAgendaGroupPreview>("/api/v1/agenda-groups/delete", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function deleteAgendaGroup(id: string) {
  return api<{
    success: true
    groupId: string
    deleted: Omit<DeleteAgendaGroupPreview["wouldDelete"], "id" | "name">
  }>(
    "/api/v1/agenda-groups/delete",
    { method: "POST", body: { id } },
  )
}

export type DeleteAgendaActivityPreview = {
  success: true
  dryRun: true
  wouldDelete: {
    id: string
    name: string
    items: number
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}

export function deleteAgendaActivityDryRun(id: string) {
  return api<DeleteAgendaActivityPreview>("/api/v1/agenda-activities/delete", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function deleteAgendaActivity(id: string) {
  return api<{
    success: true
    activityId: string
    deleted: Omit<DeleteAgendaActivityPreview["wouldDelete"], "id" | "name">
  }>(
    "/api/v1/agenda-activities/delete",
    { method: "POST", body: { id } },
  )
}

// ── lifecycle mutation callers (status moves ONLY via these POST actions) ────
export type PublishPreview = {
  success: true
  dryRun: true
  wouldPublish: { id: string; name: string; previousStatus: string }
}

export function publishItemDryRun(id: string) {
  if (isV2Api) {
    return getActivity(requireActiveEventId(), id).then((activity) => ({
      success: true as const,
      dryRun: true as const,
      wouldPublish: { id, name: activity.name, previousStatus: activity.status },
    }))
  }
  return api<PublishPreview>("/api/v1/agenda-items/publish", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function publishItem(id: string, dryRun = false) {
  if (isV2Api) {
    const eventId = requireActiveEventId()
    if (dryRun) return publishItemDryRun(id)
    return publishV2Activity(eventId, id).then(() => ({
      success: true as const,
      status: "published",
    }))
  }
  return api<{ success: true; status: string; wouldPublish?: { previousStatus: string } }>(
    "/api/v1/agenda-items/publish",
    { method: "POST", body: { id, dryRun } },
  )
}

export function unpublishItem(id: string, dryRun = false) {
  return api<{
    success: true
    status?: string
    wouldUnpublish?: { previousStatus: string }
  }>("/api/v1/agenda-items/unpublish", { method: "POST", body: { id, dryRun } })
}

export type CancelPreview = {
  success: true
  dryRun: true
  wouldCancel: { id: string; name: string; rsvpsAffected: number; channelId: string | null }
}

export function cancelItemDryRun(id: string) {
  return api<CancelPreview>("/api/v1/agenda-items/cancel", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function cancelItem(id: string, reason?: string) {
  return api<{ success: true; rsvpsAffected: number; channelId: string | null }>(
    "/api/v1/agenda-items/cancel",
    { method: "POST", body: { id, reason: reason?.trim() || undefined } },
  )
}

// ── participant mutation callers ────────────────────────────────────────────
export function checkInGuest(agendaItemId: string, guestId: string) {
  return api<{
    success: true
    cascadedOutOf: Array<{ itemName: string }>
    cascadedCount: number
  }>("/api/v1/agenda-items/check-in", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

export function checkOutGuest(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/check-out", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

// Undo a check-in entirely (back to confirmed / not checked-in).
export function cancelCheckInGuest(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/cancel-check-in", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

// Undo a check-out (back to checked-in).
export function cancelCheckOutGuest(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/cancel-check-out", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

export function setRsvp(
  agendaItemId: string,
  guestId: string,
  response: "confirmed" | "declined",
) {
  if (isV2Api) {
    const eventId = requireActiveEventId()
    if (response === "confirmed") {
      return registerEventPersonForActivity({
        eventId,
        activityId: agendaItemId,
        eventPersonId: guestId,
      })
    }
    return listAllActivityRegistrations(eventId, agendaItemId).then((page) => {
      const registration = page.registrations.find(
        (row) => row.eventPersonId === guestId && row.status !== "cancelled",
      )
      if (!registration) return { success: true as const }
      return cancelActivityRegistration({
        eventId,
        activityId: agendaItemId,
        registrationId: registration.id,
        reason: "Organizer declined the activity registration",
      })
    })
  }
  return api<{ success: true }>("/api/v1/admin/agenda-rsvp", {
    method: "POST",
    body: { agendaItemId, guestId, response },
  })
}

// "None of these" / clear a choice group: declines every item in the group for
// the guest (the unselect path for pick-one / pick-any groups).
export function declineGroup(agendaGroupId: string, guestId: string) {
  return api<{ success: true; declined?: number }>("/api/v1/admin/agenda-decline-group", {
    method: "POST",
    body: { agendaGroupId, guestId },
  })
}

export function bulkInvite(agendaItemId: string, guestIds: string[]) {
  if (isV2Api) {
    const eventId = requireActiveEventId()
    return Promise.allSettled(
      guestIds.map((eventPersonId) =>
        registerEventPersonForActivity({ eventId, activityId: agendaItemId, eventPersonId }),
      ),
    ).then((results) => ({
      success: true as const,
      added: results.filter((result) => result.status === "fulfilled").length,
      alreadyInvited: 0,
      skipped: results.filter((result) => result.status === "rejected").length,
    }))
  }
  return api<{ success: true; added: number; alreadyInvited: number; skipped: number }>(
    "/api/v1/agenda-items/bulk-invite",
    { method: "POST", body: { agendaItemId, guestIds } },
  )
}

function requireActiveEventId(): string {
  const eventId = useEvents.getState().activeEventId
  if (!eventId) throw new Error("No v2 event is active")
  return eventId
}

function slugifyActivity(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return slug || `activity-${crypto.randomUUID().slice(0, 8)}`
}

// ── per-meal catering operations ────────────────────────────────────────────
export type ActivityCatering = {
  success: true
  agendaItemName: string
  restaurant: { id: string; name: string } | null
  cateringCutoffAt: string | null
  lastExportedAt: string | null
  changedSinceExport: { guestId: string; displayName: string; updatedAt: string | null }[]
  total: number
  sharedCount: number
  notSharedCount: number
  anaphylaxisFlags: { guestId: string; displayName: string; kinds: string[] }[]
}

export function activityCateringQueryOptions(agendaItemId: string) {
  return queryOptions({
    queryKey: ["activity-catering", agendaItemId] as const,
    queryFn: () => api<ActivityCatering>("/api/v1/agenda-items/dietary-report", {
        method: "POST",
        body: { agendaItemId },
      }),
  })
}

export function setActivityCatering(
  agendaItemId: string,
  body: { cateringCutoffAt?: string | null; catererName?: string | null; catererEmail?: string | null },
) {
  return api<{ success: true }>("/api/v1/agenda-items/set-catering", {
    method: "POST",
    body: { agendaItemId, ...body },
  })
}

export function exportActivityCateringSheet(agendaItemId: string) {
  return api<{ success: true; csv?: string; agendaItemName: string }>(
    "/api/v1/agenda-items/catering-sheet",
    { method: "POST", body: { agendaItemId, format: "csv" } },
  )
}

// ── public share links (participant list / kitchen sheet) ───────────────────
// Backed by the FGA share-link actions; rendered no-login at share.attend.vip.
export type ShareLinkKind = "participants" | "kitchen"

export type ActivityShareLink = {
  kind: ShareLinkKind
  url: string
  token: string
  live: boolean
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string | null
}

export function listActivityShareLinksReq(agendaItemId: string) {
  return api<{ success: true; links: ActivityShareLink[] }>(
    "/api/v1/agenda-items/list-share-links",
    { method: "POST", body: { agendaItemId } },
  )
}

export function activityShareLinksQueryOptions(agendaItemId: string) {
  return queryOptions({
    queryKey: ["activity-share-links", agendaItemId] as const,
    queryFn: () => listActivityShareLinksReq(agendaItemId),
  })
}

export function createActivityShareLink(agendaItemId: string, kind: ShareLinkKind, ttlDays?: number) {
  return api<{ success: true; kind: ShareLinkKind; token: string; url: string; expiresAt: string | null }>(
    "/api/v1/agenda-items/create-share-link",
    { method: "POST", body: { agendaItemId, kind, ttlDays } },
  )
}

export function revokeActivityShareLink(token: string) {
  return api<{ success: true; revokedCount: number; revokedTokens: string[] }>(
    "/api/v1/agenda-items/revoke-share-link",
    { method: "POST", body: { token } },
  )
}

// ── waitlist mutation callers ───────────────────────────────────────────────
export function promoteWaitlist(agendaItemId: string, count: number) {
  if (isV2Api) {
    const eventId = requireActiveEventId()
    return Array.from({ length: Math.max(0, count) }).reduce<Promise<unknown[]>>(
      (chain) => chain.then(async (promoted) => {
        const result = await promoteNextActivityWaitlistRegistration(eventId, agendaItemId)
        return [...promoted, result]
      }),
      Promise.resolve([]),
    ).then((promoted) => ({ success: true as const, promoted }))
  }
  return api<{ success: true; promoted: unknown[] }>(
    "/api/v1/agenda-items/waitlist/promote",
    { method: "POST", body: { agendaItemId, count } },
  )
}

export function removeFromWaitlist(agendaItemId: string, guestId: string) {
  if (isV2Api) {
    const eventId = requireActiveEventId()
    return listAllActivityRegistrations(eventId, agendaItemId).then((page) => {
      const registration = page.registrations.find(
        (row) => row.eventPersonId === guestId && row.status === "waitlisted",
      )
      if (!registration) return { success: true as const }
      return cancelActivityRegistration({
        eventId,
        activityId: agendaItemId,
        registrationId: registration.id,
        reason: "Organizer removed the activity waitlist registration",
      })
    })
  }
  return api<{ success: true }>("/api/v1/agenda-items/waitlist/remove", {
    method: "POST",
    body: { agendaItemId, guestIds: [guestId] },
  })
}

export function reorderWaitlist(agendaItemId: string, guestId: string, toPosition: number) {
  return api<{ success: true }>("/api/v1/agenda-items/waitlist/reorder", {
    method: "POST",
    body: { agendaItemId, guestId, toPosition },
  })
}

// ── shared display helpers (naive local-time strings per project convention) ─
export type ChipTone =
  | "required"
  | "rsvp"
  | "invite"
  | "open"
  | "display"
  | "draft"
  | "cancelled"
  | "group"

export function attendanceChip(item: {
  rsvpMode: string
  isRequired: number
  accessMode: string
}): { tone: ChipTone; label: string } {
  if (item.rsvpMode === "required" || item.isRequired === 1)
    return { tone: "required", label: "Required" }
  if (item.accessMode === "invite-only") return { tone: "invite", label: "Invite-only" }
  if (item.rsvpMode === "rsvp") return { tone: "rsvp", label: "RSVP" }
  if (item.rsvpMode === "display") return { tone: "display", label: "Display" }
  return { tone: "open", label: "Open" }
}

export function dayKey(naive: string): string {
  return wallClockDayKey(naive) ?? ""
}

export function minutesOfDay(naive: string): number {
  const parsed = parseWallClockDateTime(naive)
  return parsed ? parsed.hour * 60 + parsed.minute : 0
}

export const parseDay = parseWallClockDate

export function formatDayLong(yyyymmdd: string): string {
  return formatWallClockDate(yyyymmdd, "weekday-long")
}

export function formatWeekday(yyyymmdd: string): string {
  const label = formatWallClockDate(yyyymmdd, "weekday-short")
  return label ? label.split(",", 1)[0] : ""
}

export function formatDayMonth(yyyymmdd: string): string {
  return formatWallClockDate(yyyymmdd, "month-day")
}

export function formatTime(naive: string): string {
  return formatWallClockTime(naive)
}

export function formatRange(startsAt: string, endsAt: string): string {
  const startDay = dayKey(startsAt)
  const endDay = dayKey(endsAt)
  if (!startDay || !endDay) return ""
  const startTime = formatTime(startsAt)
  const endTime = formatTime(endsAt)
  if (startDay === endDay) {
    return `${formatDayMonth(startDay)} · ${startTime}–${endTime}`
  }
  return `${formatDayMonth(startDay)} ${startTime} → ${formatDayMonth(endDay)} ${endTime}`
}

export function locationPath(
  locationId: string | null,
  locationsById: Map<string, LocationRow>,
  // Order of the breadcrumb. Default is root→leaf ("Venue / Room") — the
  // conventional general→specific order used in pickers and the grid. Pass
  // { leafFirst: true } to lead with the specific child ("Room / Venue").
  opts?: { leafFirst?: boolean },
): string | null {
  if (locationId == null) return null
  const names: string[] = []
  const seen = new Set<string>()
  let current = locationsById.get(locationId)
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    names.unshift(current.name)
    current = current.parentLocationId != null
      ? locationsById.get(current.parentLocationId)
      : undefined
  }
  if (names.length === 0) return null
  return (opts?.leafFirst ? [...names].reverse() : names).join(" / ")
}

// ── grouping helpers ────────────────────────────────────────────────────────
export type EmptyAgendaGroup = AgendaGroup & { startsAt: string; endsAt?: string | null }
export type DayGroup = { date: string; items: AgendaItem[]; emptyGroups: EmptyAgendaGroup[] }

export function groupByDay(items: AgendaItem[], emptyGroups: EmptyAgendaGroup[] = []): DayGroup[] {
  const map = new Map<string, DayGroup>()
  for (const i of items) {
    const day = dayKey(i.startsAt)
    if (!map.has(day)) map.set(day, { date: day, items: [], emptyGroups: [] })
    map.get(day)!.items.push(i)
  }
  for (const g of emptyGroups) {
    const day = dayKey(g.startsAt)
    if (!map.has(day)) map.set(day, { date: day, items: [], emptyGroups: [] })
    map.get(day)!.emptyGroups.push(g)
  }
  return Array.from(map.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((day) => ({
      ...day,
      items: day.items.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
      emptyGroups: day.emptyGroups.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    }))
}

export function clusterByStartTime(
  items: AgendaItem[],
): { startsAt: string; endsAt: string | null; items: AgendaItem[] }[] {
  const slots: { startsAt: string; endsAt: string | null; items: AgendaItem[] }[] = []
  for (const it of items) {
    const last = slots[slots.length - 1]
    if (last && last.startsAt === it.startsAt) {
      last.items.push(it)
      if (it.endsAt > (last.endsAt ?? "")) last.endsAt = it.endsAt
    } else {
      slots.push({ startsAt: it.startsAt, endsAt: it.endsAt, items: [it] })
    }
  }
  return slots
}

export function commonSlotGroup(
  items: AgendaItem[],
  groupsById: Map<string, AgendaGroup>,
): AgendaGroup | null {
  const groupId = items[0]?.groupId
  if (groupId == null) return null
  if (!items.every((item) => item.groupId === groupId)) return null
  return groupsById.get(groupId) ?? null
}

export function commonSlotActivity(
  items: AgendaItem[],
  activitiesById: Map<string, AgendaActivity>,
): AgendaActivity | null {
  const activityId = items[0]?.activityId
  if (activityId == null) return null
  if (!items.every((item) => item.activityId === activityId)) return null
  return activitiesById.get(activityId) ?? null
}

export function choiceLabel(group: AgendaGroup, count: number): string {
  if (group.selectionRule === "multi") return `Pick any of ${count}`
  return `Pick one of ${count}`
}
